import {Box, Container, Desktop, StyleProps} from '@miro-site/core'
import React, {useRef, useEffect, useState} from 'react'
import styled, {css} from 'styled-components'
import CategoryNav, {Button as CategoryNavButton} from '../category-nav/CategoryNav'
import {StatusMessages} from '../search/components/SearchDropdown'
import Search, {SearchResult, SearchWrapper as SearchInputWrapper} from '../search/Search'

export type CategoryGroupItem = {
  title: string
  href: string
  slug: string
}

export type CategoryGroup = {
  title: string
  categories: CategoryGroupItem[]
}

export interface TopBarProps {
  rootGroups: CategoryGroup[]
  onFocus?: React.FocusEventHandler<HTMLElement>
  onBlur?: React.FocusEventHandler<HTMLAnchorElement | HTMLButtonElement>
  searchResult?: SearchResult[]
  isSearching?: boolean
  onCategoryNavOpened?: () => void
  onSearch: (term: string) => void
  onSelectTemplate?: (template: SearchResult) => void
  backButton?: React.ReactElement
  categoriesButtonLabel: string
  onSelectCategory?: (category: CategoryGroupItem) => void
  isSticky?: boolean
  statusMessages: StatusMessages
}

const TopBarWrapper = styled(Box).attrs<{$isFixed?: boolean}>(
  ({theme, $isFixed, $styles = {}}) => ({
    $styles: {
      ...($isFixed
        ? {
            position: 'fixed',
            paddingTop: [8, 17, 17, 17],
            paddingBottom: [8, 17, 17, 17],
            boxShadow: theme.templates.elevation.topBar,
            backgroundColor: 'primaryWhite',
            top: 0,
            right: 0,
            left: 0,
            zIndex: 700,
          }
        : {
            position: 'relative',
          }),
      ...$styles,
    },
  }),
)<{$isFixed?: boolean}>({})

const TopBarSearch = styled(Search)<{$isActive?: boolean; $isFixed?: boolean}>`
  ${({$isActive, $isFixed, theme}) =>
    $isActive &&
    $isFixed &&
    css`
      ${SearchInputWrapper} {
        box-shadow: ${theme.templates.elevation.fixedSearch};
      }
    `}
`

const TopBar: React.FC<TopBarProps> = ({
  rootGroups,
  onSearch,
  searchResult,
  statusMessages,
  isSearching,
  onCategoryNavOpened,
  onSelectTemplate,
  onSelectCategory,
  backButton,
  categoriesButtonLabel,
  isSticky = false,
}) => {
  const [categoryNavOpened, setCategoryNavOpened] = useState(false)
  const [isSearchActive, setSearchActive] = useState(false)

  const navRef = useRef<HTMLElement>(null)
  const navButtonRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null)

  const handleClick = (e: CustomEvent) => {
    const clickOnNavButton = navButtonRef.current && navButtonRef.current.contains(e.target as Node)

    if (clickOnNavButton) {
      return
    }

    const clickOutsideNav = navRef.current && !navRef.current.contains(e.target as Node)

    if (clickOutsideNav) {
      setCategoryNavOpened(false)
    }
  }

  useEffect(() => {
    if (!categoryNavOpened) {
      return
    }

    if (onCategoryNavOpened) {
      onCategoryNavOpened()
    }

    document.addEventListener('click', handleClick as EventListener, true)
    window.addEventListener('focusin', onFocusCategoryDown)

    return () => {
      document.removeEventListener('click', handleClick as EventListener, true)
      document.removeEventListener('keydown', onFocusCategoryDown)
    }
  }, [categoryNavOpened])

  const onKeyDownHandler = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()

      setCategoryNavOpened(!categoryNavOpened)
    }
  }

  const onFocusCategoryDown = function () {
    if (
      navRef.current &&
      document.activeElement !== navButtonRef.current &&
      !navRef.current.contains(document.activeElement)
    ) {
      setCategoryNavOpened(false)
    }
  }

  const searchContainerStyles: StyleProps = {
    width: !isSearchActive ? [40, 394, 394, 394] : '100%',
    marginLeft: ['auto', 0, 0, 0],
    position: 'relative',
  }

  return (
    <TopBarWrapper $isFixed={isSticky}>
      <Container>
        <Box
          $styles={{
            justifyContent: backButton ? 'flex-start' : 'center',
            alignItems: 'center',
            display: 'flex',
            position: 'relative',
          }}
        >
          <>
            {backButton && (
              <Desktop
                $mr="auto"
                $styles={{
                  position: isSearchActive ? 'absolute' : 'relative',
                }}
              >
                {backButton}
              </Desktop>
            )}
            <CategoryNavButton
              ref={navButtonRef}
              $mr={12}
              isOpened={categoryNavOpened}
              onClick={() => {
                setCategoryNavOpened(!categoryNavOpened)
              }}
              onKeyDown={onKeyDownHandler}
              isSearchActive={isSearchActive}
            >
              {categoriesButtonLabel}
            </CategoryNavButton>
          </>
          <CategoryNav
            ref={navRef}
            isVisible={categoryNavOpened}
            rootGroups={rootGroups}
            onMobilePopupClose={() => {
              setCategoryNavOpened(false)
            }}
            onCategoryOpen={category => {
              setCategoryNavOpened(false)

              if (onSelectCategory) {
                onSelectCategory(category)
              }
            }}
          />
          <Box $styles={searchContainerStyles}>
            <TopBarSearch
              $isActive={isSearchActive}
              $isFixed={isSticky}
              onSearch={onSearch}
              searchResult={searchResult}
              statusMessages={statusMessages}
              isSearching={isSearching}
              onSelectTemplate={onSelectTemplate}
              onFocusChanged={isFocused => {
                setSearchActive(isFocused)

                if (isFocused) {
                  setCategoryNavOpened(false)
                }
              }}
            />
          </Box>
        </Box>
      </Container>
    </TopBarWrapper>
  )
}

export default TopBar
